import { lazy, Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingWhatsApp from '../../components/feature/FloatingWhatsApp';
import HeroSection from './components/HeroSection';
import ScrollingTicker from './components/ScrollingTicker';
import TrustStats from './components/TrustStats';
import { getOrganizationSchema, SCHEMA_SITE_URL } from '@/constants/schema';

// Lazy-load below-the-fold sections so they don't block initial page load
const WhoWeAre = lazy(() => import('./components/WhoWeAre'));
const What28YearsMeans = lazy(() => import('./components/What28YearsMeans'));
const ProductCategories = lazy(() => import('./components/ProductCategories'));
const Industries = lazy(() => import('./components/Industries'));
const Coverage = lazy(() => import('./components/Coverage'));
const ComingSoon = lazy(() => import('./components/ComingSoon'));
const WhatsAppCTASection = lazy(() => import('./components/WhatsAppCTASection'));

const SectionLoader = () => (
  <div className="w-full py-20 flex items-center justify-center">
    <div className="w-8 h-8 border-3 border-brand-green border-t-transparent rounded-full animate-spin" />
  </div>
);

const HOME_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    getOrganizationSchema(),
    {
      '@type': 'WebPage',
      '@id': `${SCHEMA_SITE_URL}/#webpage`,
      url: SCHEMA_SITE_URL,
      name: 'Wholesale Disposable Gloves Oman | Trusted Supplier Muscat | omandisposables.com',
      description:
        "Oman's trusted wholesale glove supplier. Vinyl, latex, nitrile gloves for medical, food, industrial and lab use across Oman and GCC. Bulk orders via WhatsApp from Muscat.",
      inLanguage: ['en', 'ar'],
      isPartOf: { '@id': `${SCHEMA_SITE_URL}/#organization` },
      breadcrumb: { '@id': `${SCHEMA_SITE_URL}/#breadcrumb` },
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${SCHEMA_SITE_URL}/#business`,
      name: 'omandisposables.com',
      url: SCHEMA_SITE_URL,
      description:
        "Oman's trusted wholesale glove supplier. Supplying vinyl, latex, nitrile gloves for medical, food, industrial and lab use across Oman and GCC.",
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'OM',
        addressLocality: 'Muscat',
      },
      areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Sunday'],
          opens: '08:00',
          closes: '18:00',
        },
      ],
      sameAs: [SCHEMA_SITE_URL],
    },
    {
      '@type': 'WebSite',
      '@id': `${SCHEMA_SITE_URL}/#website`,
      url: SCHEMA_SITE_URL,
      name: 'omandisposables.com',
      description:
        "Oman's wholesale disposable gloves supplier — medical, food grade, lab & industrial.",
      publisher: {
        '@id': `${SCHEMA_SITE_URL}/#business`,
      },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${SCHEMA_SITE_URL}/?s={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${SCHEMA_SITE_URL}/#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SCHEMA_SITE_URL,
        },
      ],
    },
  ],
};

export default function HomePage() {
  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(HOME_SCHEMA) }}
      />
      <Navbar />
      <HeroSection />
      <ScrollingTicker />
      <TrustStats />
      <Suspense fallback={<SectionLoader />}>
        <WhoWeAre />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <What28YearsMeans />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ProductCategories />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Industries />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Coverage />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ComingSoon />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <WhatsAppCTASection />
      </Suspense>
      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
